/**
 * Handles drag-based scrolling on the lane.
 *
 * @property {import('vue').Component} component - The Vue component instance.
 * @property {boolean} isDragging - Whether a drag is currently active.
 * @property {number} startX - Initial X position at drag start.
 * @property {number} startY - Initial Y position at drag start.
 * @property {number} lastWalk - Last calculated scroll delta.
 * @property {number} dragDistance - Total distance dragged.
 * @property {number} scrollLeft - Initial scrollLeft position.
 * @property {number} scrollTop - Initial scrollTop position.
 * @method bindEvents - Binds mouse events to the lane.
 * @method onMouseDown - Handles mousedown event to start drag.
 * @method onMouseLeave - Handles mouseleave event to end drag.
 * @method onMouseUp - Handles mouseup event to finalize drag.
 * @method onMouseMove - Handles mousemove event to perform scrolling.
 * @method onClick - Cancels click events after dragging.
 * @method destroy - Removes all bound event listeners.
 */
export default class LaneDraggable {
	constructor(component) {
		this.component = component;
		this.isDragging = false;
		this.startX = 0;
		this.startY = 0;
		this.lastWalk = 0;
		this.dragDistance = 0;
		this.scrollLeft = 0;
		this.scrollTop = 0;
		this.bindEvents();
	}

	bindEvents() {
		const lane = this.component.lane;
		this.onMouseDown = this.onMouseDown.bind(this);
		this.onMouseLeave = this.onMouseLeave.bind(this);
		this.onMouseUp = this.onMouseUp.bind(this);
		this.onMouseMove = this.onMouseMove.bind(this);
		this.onClick = this.onClick.bind(this);

		lane.addEventListener('mousedown', this.onMouseDown);
		lane.addEventListener('mouseleave', this.onMouseLeave);
		lane.addEventListener('mouseup', this.onMouseUp);
		lane.addEventListener('mousemove', this.onMouseMove);
		// Hier fangen wir Click-Events früh ab
		lane.addEventListener('click', this.onClick, true);
	}

	onMouseDown(e) {
		const lane = this.component.lane;
		this.isDragging = true;
		this.dragDistance = 0;
		this.lastWalk = 0;

		if (this.component.orientation === 'vertical') {
			this.startY = e.pageY - lane.offsetTop;
			this.scrollTop = lane.scrollTop;
		} else {
			this.startX = e.pageX - lane.offsetLeft;
			this.scrollLeft = lane.scrollLeft;
		}

		lane.classList.add('is-animating');

		// Pause autoscroll
		if (this.component.autoscroll) {
			this.component.autoscroll.pauseAutoscroll();
		}
	}

	onMouseLeave() {
		if (this.isDragging) {
			this.isDragging = false;
			this.component.lane.classList.remove('is-animating');
		}

		// Resume autoscroll
		if (this.component.autoscroll) {
			if(!this.component.autoScrollItemIsPausedMouseOver)
				this.component.autoscroll.resumeAutoscroll();
		}
	}

	onMouseUp(e) {
		if (this.isDragging && this.dragDistance > 5) {
			// Wir verhindern das "echte" Klicken
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
		}

		this.component.lane.classList.remove('pointer-events-none-while-dragging');

		this.isDragging = false;
		this.dragDistance = 0;
		this.lastWalk = 0;
		this.component.lane.classList.remove('is-animating');

		// Resume autoscroll
		if (this.component.autoscroll) {
			//this.component.autoscroll.resumeAutoscroll();
		}
	}

	onMouseMove(e) {
		if (!this.isDragging) return;
		e.preventDefault(); // Verhindert z.B. Text-Selektionen

		const lane = this.component.lane;
		let walk = 0;

		if (this.component.orientation === 'vertical') {
			const y = e.pageY - lane.offsetTop;
			walk = y - this.startY;
			lane.scrollTop = this.scrollTop - walk;
		} else {
			const x = e.pageX - lane.offsetLeft;
			walk = x - this.startX;
			lane.scrollLeft = this.scrollLeft - walk;
		}

		const walkChange = walk - this.lastWalk;
		this.dragDistance += Math.abs(walkChange);
		this.lastWalk = walk;

		if (
			this.dragDistance > 5 &&
			!lane.classList.contains('pointer-events-none-while-dragging')
		) {
			lane.classList.add('pointer-events-none-while-dragging');
		}
	}

	onClick(e) {
		// Falls sich herausstellt, dass tatsächlich gedragt wurde, unterbinden wir das Klicken
		if (this.dragDistance > 5) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
		}
	}

	destroy() {
		const lane = this.component.lane;
		lane.removeEventListener('mousedown', this.onMouseDown);
		lane.removeEventListener('mouseleave', this.onMouseLeave);
		lane.removeEventListener('mouseup', this.onMouseUp);
		lane.removeEventListener('mousemove', this.onMouseMove);
		lane.removeEventListener('click', this.onClick, true);
	}
}
